import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core'
import { provideAnimations } from '@angular/platform-browser/animations'
import { provideRouter, Router, withNavigationErrorHandler } from '@angular/router'
import * as Sentry from '@sentry/angular'
import { registerLocaleData } from '@angular/common'

import { routes } from './routes'
import { environment } from '../environments/environment'
import {
  API_HOST,
  API_PREFIX,
  GET_IS_ROOT,
  GET_PERMISSIONS,
  HANDLE_AUTH_ERROR,
  LOG_LEVEL,
  provideSentryLogSink,
} from '@seiri/shared/frontend'
import { handleAuthError } from './auth/services/auth-error.handler'
import { AuthService } from './auth/services/auth.service'
import { recoverFromStaleChunk } from './shared/services/stale-build.service'
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog'
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, MatButtonToggleDefaultOptions } from '@angular/material/button-toggle'
import { provideUiTour } from 'ngx-ui-tour-md-menu'
import localeSk from '@angular/common/locales/sk'
import localeCz from '@angular/common/locales/cs'

const { sentry, apiHost, apiPrefix, production } = environment

registerLocaleData(localeSk)
registerLocaleData(localeCz)

const sentryProvides = [
  {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler(),
  },
  {
    provide: Sentry.TraceService,
    deps: [Router],
  },
  provideAppInitializer(() => {
    inject(Sentry.TraceService)
  }),
  // Routes LoggerService records into Sentry: breadcrumbs for everything, captureException for
  // errors. Without this the logger still works, it just has nowhere to send anything.
  provideSentryLogSink(),
]

const defaultLocale = 'sk-SK'
const supportedLocales = ['en-US', 'sk-SK', 'cs-CZ']
const normalizeLocale = (locale: string): string => {
  try {
    return Intl.getCanonicalLocales(locale)[0] || defaultLocale
  } catch {
    return defaultLocale
  }
}
function getBrowserLocale(): string {
  const browserLocales = navigator.languages?.length ? navigator.languages : [navigator.language]

  for (const locale of browserLocales) {
    const normalized = normalizeLocale(locale)

    if (supportedLocales.includes(normalized)) {
      return normalized
    }

    const languageOnlyMatch = supportedLocales.find((supported) => supported.split('-')[0] === normalized.split('-')[0])

    if (languageOnlyMatch) {
      return languageOnlyMatch
    }
  }
  return defaultLocale
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes, withNavigationErrorHandler(recoverFromStaleChunk)),
    provideUiTour({
      enableBackdrop: true,
      backdropConfig: {
        offset: 10,
      },
    }),
    provideAnimations(),
    ...(sentry ? sentryProvides : []),
    {
      // Console threshold. Everything below it is still recorded as a Sentry breadcrumb; raise it
      // at runtime with localStorage.setItem('seiri:log', 'debug').
      provide: LOG_LEVEL,
      useValue: production ? 'warn' : 'debug',
    },
    {
      provide: LOCALE_ID,
      useFactory: getBrowserLocale,
    },
    {
      provide: API_HOST,
      useValue: apiHost,
    },
    {
      provide: API_PREFIX,
      useValue: apiPrefix,
    },
    {
      provide: HANDLE_AUTH_ERROR,
      useFactory: handleAuthError,
      deps: [AuthService],
    },
    {
      provide: GET_PERMISSIONS,
      useFactory: (authService: AuthService) => authService.permissions,
      deps: [AuthService],
    },
    {
      provide: GET_IS_ROOT,
      useFactory: (authService: AuthService) => authService.root,
      deps: [AuthService],
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        backdropClass: 'dialog-bg',
      },
    },
    {
      // toggles are a segmented control whose checked segment is filled; no checkmark (material-overrides.css)
      provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
      useValue: {
        hideSingleSelectionIndicator: true,
        hideMultipleSelectionIndicator: true,
      } satisfies MatButtonToggleDefaultOptions,
    },
  ],
}
